import type { JSX } from 'react';
import { styled } from '@mui/material';
import {
  CircleIcon,
  DiamondIcon,
  DropletIcon,
  HexagonIcon,
  HourglassIcon,
  PentagonIcon,
  SquareIcon,
  TriangleIcon,
  type LucideIcon,
} from 'lucide-react';
import type { Assay } from '@zero-dash/types';
import { corePalette } from '@/themes/colours';
import { DISEASE_BURDEN_MARKER_SIZE } from '@/constants/PatientDashboard/patientTimeline';
import type { ChartAssay } from '@/types/PatientDashboard/PatientTimeline.types';

const STROKE_WIDTH = 1.5;
const ARTWORK_BOX_SIZE = 10;
const ARTWORK_STROKE_MARGIN = 0.75;
const ARTWORK_VIEW_BOX = `${-ARTWORK_STROKE_MARGIN} ${-ARTWORK_STROKE_MARGIN}`
  + ` ${ARTWORK_BOX_SIZE + 2 * ARTWORK_STROKE_MARGIN}`
  + ` ${ARTWORK_BOX_SIZE + 2 * ARTWORK_STROKE_MARGIN}`;

const ICON_GLYPH_STYLE = { display: 'block', flexShrink: 0 } as const;

const ArtworkGlyph = styled('svg')({
  display: 'block',
  flexShrink: 0,
  overflow: 'hidden',
});

interface IAssayArtwork {
  path: string;
  viewBox?: string;
}

type AssayGlyph = LucideIcon | IAssayArtwork;

const isArtwork = (glyph: AssayGlyph): glyph is IAssayArtwork => 'path' in glyph;

/* eslint-disable @typescript-eslint/naming-convention -- keys are the LabMatrix `lbAssays`
   vocabulary, which sets their casing (same reason its own constants file disables this). */
const ASSAY_GLYPHS: Record<Assay, AssayGlyph> = {
  WGS_Only: SquareIcon,
  Low_pass_NGS: CircleIcon,
  LB_Panel: DropletIcon,
  'H&E': PentagonIcon,
  ddPCR: TriangleIcon,
  short_scRNASeq: HourglassIcon,
  LB_Germline_WGS: HexagonIcon,
  Olink: {
    path: 'M1.05 2.4A1.35 1.35 0 1 0 3.75 2.4A1.35 1.35 0 1 0 1.05 2.4Z'
      + 'M6.25 2.4A1.35 1.35 0 1 0 8.95 2.4A1.35 1.35 0 1 0 6.25 2.4Z'
      + 'M1.05 7.6A1.35 1.35 0 1 0 3.75 7.6A1.35 1.35 0 1 0 1.05 7.6Z'
      + 'M6.25 7.6A1.35 1.35 0 1 0 8.95 7.6A1.35 1.35 0 1 0 6.25 7.6Z',
  },
  MACSima: {
    viewBox: '0 0 8 10',
    path: 'M4 0.7L7.3 3L4.9 5L7.3 7L4 9.3L0.7 7L3.1 5L0.7 3Z',
  },
  Xenium: {
    path: 'M0.9 0.9L5 3.9L9.1 0.9L6.1 5L9.1 9.1L5 6.1L0.9 9.1L3.9 5Z',
  },
  Flow: {
    path: 'M0.4 3.5C1.55 0.66 3.85 0.66 5 3.5C6.15 6.34 8.45 6.34 9.6 3.5'
      + 'L9.6 6.5C8.45 9.34 6.15 9.34 5 6.5C3.85 3.66 1.55 3.66 0.4 6.5Z',
  },
  STAMP: {
    path: 'M7.5 5C8.881 5 10 3.881 10 2.5C10 1.119 8.881 0 7.5 0H2.5C1.119 0 0 1.119 0 2.5'
      + 'C0 3.881 1.119 5 2.5 5C1.119 5 0 6.119 0 7.5C0 8.881 1.119 10 2.5 10H7.5C8.881 10'
      + ' 10 8.881 10 7.5C10 6.119 8.881 5 7.5 5Z',
  },
  long_scRNASeq: {
    path: 'M5 1.1C5 0.492487 4.50751 0 3.9 0H1.1C0.492487 0 0 0.492486 0 1.1V3.93597C0'
      + ' 4.54349 0.492487 5.03595 1.1 5.03595H3.9C4.50751 5.03595 5 5.52845 5 6.13595V8.9'
      + 'C5 9.5075 5.4925 10 6.1 10H8.9C9.5075 10 10 9.5075 10 8.9V6.064C10 5.4565 9.5075'
      + ' 4.96403 8.9 4.96403H6.1C5.4925 4.96403 5 4.47154 5 3.86403V1.1Z',
  },
  LB_Tumour_WGS: {
    path: 'M7.92015 10C6.57235 7.69225 3.42766 7.69225 2.07987 10C2.80931 7.69225 2.30786'
      + ' 7.19085 0 7.92025C2.30786 6.5725 2.30786 3.42801 0 2.0803C2.30786 2.80916 2.80931'
      + ' 2.30774 2.07987 0C3.42766 2.30774 6.57235 2.30774 7.92015 0C7.1912 2.30774 7.6927'
      + ' 2.80916 10 2.0803C7.6927 3.42801 7.6927 6.5725 10 7.92025C7.6927 7.19085 7.1912'
      + ' 7.69225 7.92015 10Z',
  },
};
/* eslint-enable @typescript-eslint/naming-convention */
const GLYPHS: Partial<Record<ChartAssay, AssayGlyph>> = ASSAY_GLYPHS;

const FALLBACK_GLYPH: AssayGlyph = DiamondIcon;

interface IProps {
  assay: ChartAssay;
  colour: string;
  isDetected: boolean;
}

export default function AssayMarker({ assay, colour, isDetected }: IProps): JSX.Element {
  const glyph = GLYPHS[assay] ?? FALLBACK_GLYPH;
  const fill = isDetected ? colour : corePalette.white;

  if (!isArtwork(glyph)) {
    const Icon = glyph;
    return (
      <Icon
        size={DISEASE_BURDEN_MARKER_SIZE}
        color={colour}
        fill={fill}
        strokeWidth={STROKE_WIDTH}
        absoluteStrokeWidth
        aria-hidden="true"
        style={ICON_GLYPH_STYLE}
      />
    );
  }

  return (
    <ArtworkGlyph
      width={DISEASE_BURDEN_MARKER_SIZE}
      height={DISEASE_BURDEN_MARKER_SIZE}
      viewBox={glyph.viewBox ?? ARTWORK_VIEW_BOX}
      preserveAspectRatio="xMidYMid meet"
      fill={fill}
      stroke={colour}
      strokeWidth={STROKE_WIDTH}
      aria-hidden="true"
    >
      <path d={glyph.path} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </ArtworkGlyph>
  );
}
